import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button (opens code in /editorHTML)
function TryItButton({ html, css = "", js }) {
  const handleClick = () => {
    const codeData = { html, css, js };
    const encodedCode = encodeURIComponent(JSON.stringify(codeData));
    window.open(`/editorHTML?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Events() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">
        🎯 JavaScript Events — Practical Examples
      </h1>

      {/* 🔹 Intro */}
      <div
        className="Key_point_container"
        style={{
          backgroundColor: "#dbeafe",
        }}
      >
        <p>
          JavaScript <b>events</b> make your web pages interactive! You can
          respond to user actions such as clicks, typing, mouse movement, form
          submissions, and more using <code>addEventListener()</code>.
        </p>
      </div>

      {/* 🔹 Event Examples */}
      <Section
        title="1️⃣ Click Event"
        html={`<button id="clickBtn">Click Me</button>`}
        js={`document.getElementById("clickBtn").addEventListener("click", function() {
  alert("You clicked the button!");
});`}
        description="Triggers an alert when the button is clicked."
      />

      <Section
        title="2️⃣ Mouse Over Event"
        html={`<h2 id="hoverText">Hover over me!</h2>`}
        js={`document.getElementById("hoverText").addEventListener("mouseover", function() {
  this.style.color = "red";
  this.innerText = "Mouse Over Event Triggered!";
});`}
        description="Changes text and color when hovered."
      />

      <Section
        title="3️⃣ Input Event"
        html={`<input id="nameInput" placeholder="Type your name" />
<p id="displayName"></p>`}
        js={`document.getElementById("nameInput").addEventListener("input", function() {
  document.getElementById("displayName").innerText = "Hello, " + this.value;
});`}
        description="Displays live text while typing."
      />

      <Section
        title="4️⃣ Form Submit Event"
        html={`<form id="myForm">
  <label>Enter Name:</label><br/>
  <input id="name" /><br/><br/>
  <button type="submit">Submit</button>
</form>`}
        js={`document.getElementById("myForm").addEventListener("submit", function(event) {
  event.preventDefault();
  const name = document.getElementById("name").value;
  alert("Form submitted! Name: " + name);
});`}
        description="Prevents page reload and shows the entered name."
      />

      <Section
        title="5️⃣ Keyboard Event"
        html={`<input id="keyInput" placeholder="Press any key..." />
<p id="keyDisplay"></p>`}
        js={`document.getElementById("keyInput").addEventListener("keydown", function(e) {
  document.getElementById("keyDisplay").innerText = "You pressed: " + e.key;
});`}
        description="Detects key presses and displays the key."
      />

      <Section
        title="6️⃣ Window Resize Event"
        html={`<p id="sizeInfo">Resize the window!</p>`}
        js={`window.addEventListener("resize", function() {
  document.getElementById("sizeInfo").innerText =
    "Width: " + window.innerWidth + " | Height: " + window.innerHeight;
});`}
        description="Shows the window size when resizing."
      />

      {/* 🔹 Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Summary</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <b>addEventListener()</b> for cleaner code.
          </li>
          <li>
            Always use <b>event.preventDefault()</b> in form handling.
          </li>
          <li>
            Events can react to user actions like clicks, input, and more.
          </li>
          <li>Try combining multiple event types for dynamic interfaces.</li>
        </ul>
      </div>
    </div>
  );
}

// 🔹 Reusable Section Component (keeps your CSS card design)
function Section({ title, html, js, description }) {
  return (
    <div className="tutorial-section">
      <h2 className="section-title">{title}</h2>
      <div className="code-demo-box">
        <CodeCopyBlock code={js} />
        <TryItButton html={html} js={js} />
      </div>
      <h3 style={{ marginTop: "1rem" }}>Output:</h3>
      <div
        style={{
          fontSize: "1.1rem",
          padding: "10px",
          border: "2px dashed #2563eb",
          borderRadius: "8px",
          background: "#f8fafc",
        }}
      >
        <pre>{description}</pre>
      </div>
    </div>
  );
}
